import { useState } from "react";
import type { QueueFile } from "./use-upload-queue";
import type { FileProgress } from "./use-upload";
import { formatSpeed } from "./use-upload";

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

type Props = {
  items: QueueFile[];
  progressById: Record<string, FileProgress>;
  uploading: boolean;
  onRemove: (id: string) => void;
  onRename: (id: string, name: string) => void;
};

export function FileQueue({ items, progressById, uploading, onRemove, onRename }: Props) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  if (!items.length) return null;

  return (
    <div className="overflow-hidden rounded-lg border border-ink/10 bg-cream shadow-card">
      <div className="hidden border-b border-ink/10 bg-cream-deep/60 px-4 py-2 font-mono text-label uppercase text-tertiary sm:grid sm:grid-cols-[minmax(0,1fr)_auto_auto_auto] sm:gap-x-4">
        <span>File</span>
        <span>Size</span>
        <span>Status</span>
        <span className="text-right">Actions</span>
      </div>
      <ul className="max-h-80 divide-y divide-ink/8 overflow-y-auto">
        {items.map((item) => {
          const prog = progressById[item.id];
          const isEditing = editingId === item.id;
          const hasError = item.status === "too_large" || item.status === "duplicate";

          let statusLabel = "Ready";
          let statusClass = "text-secondary";
          if (item.status === "too_large") {
            statusLabel = "Too large";
            statusClass = "text-error";
          } else if (item.status === "duplicate") {
            statusLabel = "Duplicate";
            statusClass = "text-error";
          } else if (prog?.status === "uploading") {
            statusLabel = `${prog.pct}% · ${formatSpeed(prog.speedBps)}`;
            statusClass = "text-primary";
          } else if (prog?.status === "done") {
            statusLabel = "Uploaded";
            statusClass = "text-success";
          } else if (prog?.status === "failed") {
            statusLabel = prog.error ? `Failed — ${prog.error}` : "Failed";
            statusClass = "text-error";
          }

          return (
            <li
              key={item.id}
              className={`px-4 py-3 text-body-sm sm:grid sm:grid-cols-[minmax(0,1fr)_auto_auto_auto] sm:items-center sm:gap-x-4 ${
                hasError ? "bg-error/5" : ""
              }`}
            >
              <div className="min-w-0">
                {isEditing ? (
                  <input
                    autoFocus
                    className="w-full rounded border border-ink/20 bg-app px-2 py-1 text-body-sm"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => {
                      onRename(item.id, draft);
                      setEditingId(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                      if (e.key === "Escape") setEditingId(null);
                    }}
                  />
                ) : (
                  <>
                    <p className="truncate font-medium text-primary">{item.displayName}</p>
                    {item.relativePath !== item.displayName && (
                      <p className="truncate text-caption text-muted">{item.relativePath}</p>
                    )}
                    {prog?.status === "uploading" && (
                      <div
                        className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-ink/10"
                        role="progressbar"
                        aria-valuenow={Math.max(0, prog.pct)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Upload progress for ${item.displayName}`}
                      >
                        <div
                          className="h-full rounded-full bg-coral transition-all duration-fast"
                          style={{ width: `${Math.max(0, prog.pct)}%` }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="mt-2 flex flex-col gap-2 sm:mt-0 sm:contents">
                <span className="shrink-0 text-secondary tabular-nums">
                  <span className="font-mono text-caption uppercase text-tertiary sm:hidden">Size · </span>
                  {formatBytes(item.file.size)}
                </span>
                <span className={`shrink-0 whitespace-nowrap font-mono text-caption sm:text-xs ${statusClass}`}>
                  <span className="uppercase text-tertiary sm:hidden">Status · </span>
                  {statusLabel}
                </span>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-1">
                  {!uploading && item.status === "ready" && (
                    <button
                      type="button"
                      className="text-caption text-tertiary transition-colors duration-fast hover:text-primary"
                      onClick={() => {
                        setEditingId(item.id);
                        setDraft(item.displayName);
                      }}
                    >
                      Rename
                    </button>
                  )}
                  {!uploading && (
                    <button
                      type="button"
                      className="text-caption text-tertiary transition-colors duration-fast hover:text-error"
                      onClick={() => onRemove(item.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
